import type { OnboardingFormData, ValidationResult } from '../types/onboarding.types';
import { DOMAIN_REGEX, EMAIL_REGEX, ERROR_MESSAGES } from './constants';

/**
 * Validation functions for onboarding steps
 */

export const validateCompanyStep = (data: OnboardingFormData): ValidationResult => {
  if (!data.company.trim()) {
    return { isValid: false, error: ERROR_MESSAGES.COMPANY_NAME_REQUIRED };
  }

  if (!data.domain.trim()) {
    return { isValid: false, error: ERROR_MESSAGES.DOMAIN_REQUIRED };
  }

  if (data.domain.trim() && !DOMAIN_REGEX.test(data.domain.trim())) {
    return { isValid: false, error: ERROR_MESSAGES.DOMAIN_INVALID };
  }

  return { isValid: true };
};

export const validateArrayField = (field: string[], fieldName: string): ValidationResult => {
  if (field.length === 0) {
    const item = fieldName.toLowerCase().replace(/s$/, '');
    return {
      isValid: false,
      error: `Please add at least one ${item}`,
    };
  }
  return { isValid: true };
};

export const validateEmailSettings = (
  emailSettings: OnboardingFormData['emailSettings']
): ValidationResult => {
  if (!emailSettings.signature.trim()) {
    return { isValid: false, error: ERROR_MESSAGES.EMAIL_SIGNATURE_REQUIRED };
  }

  if (
    emailSettings.communicationStyle === 'OTHER' &&
    !emailSettings.communicationStyleText?.trim()
  ) {
    return { isValid: false, error: ERROR_MESSAGES.COMMUNICATION_STYLE_TEXT_REQUIRED };
  }

  return { isValid: true };
};

export const validateInviteUsers = (
  inviteUsers: OnboardingFormData['inviteUsers']
): ValidationResult => {
  if (inviteUsers.length === 0) {
    return { isValid: false, error: ERROR_MESSAGES.INVITE_USER_REQUIRED };
  }

  return { isValid: true };
};

export const validateInviteUserData = (
  fullName: string,
  email: string,
  existingUsers: OnboardingFormData['inviteUsers']
): ValidationResult => {
  if (!fullName.trim() || !email.trim()) {
    return { isValid: false, error: ERROR_MESSAGES.NAME_REQUIRED };
  }

  if (!EMAIL_REGEX.test(email.trim())) {
    return { isValid: false, error: ERROR_MESSAGES.INVALID_EMAIL };
  }

  const emailExists = existingUsers.some(
    (user) => user.email.toLowerCase() === email.toLowerCase()
  );

  if (emailExists) {
    return { isValid: false, error: ERROR_MESSAGES.EMAIL_EXISTS };
  }

  return { isValid: true };
};

export const validateLocationInput = (
  input: string,
  existingLocations: string[]
): ValidationResult => {
  const trimmed = input.trim();
  if (!trimmed) {
    return { isValid: false, error: ERROR_MESSAGES.LOCATION_REQUIRED };
  }

  if (existingLocations.includes(trimmed)) {
    return { isValid: false, error: 'This location has already been added' };
  }

  return { isValid: true };
};

export const validateTagInput = (
  input: string,
  existingTags: string[],
  fieldName: string
): ValidationResult => {
  const trimmed = input.trim();
  if (!trimmed) {
    return { isValid: false, error: `Please enter a ${fieldName.toLowerCase().slice(0, -1)}` };
  }

  if (existingTags.includes(trimmed)) {
    return {
      isValid: false,
      error: `This ${fieldName.toLowerCase().slice(0, -1)} has already been added`,
    };
  }

  return { isValid: true };
};

export const validateStepData = (stepId: string, data: OnboardingFormData): ValidationResult => {
  switch (stepId) {
    case 'company':
      return validateCompanyStep(data);

    case 'locations':
      return validateArrayField(data.locations, 'locations');

    case 'services':
      return validateArrayField(data.services, 'services');

    case 'size':
      return validateArrayField(data.size, 'sizes');

    case 'industries':
      return validateArrayField(data.industries, 'industries');

    case 'jobTitles':
      return validateArrayField(data.jobTitles, 'job titles');

    case 'integrations':
      return { isValid: true }; // Optional step

    case 'invite':
      return validateInviteUsers(data.inviteUsers);

    default:
      return { isValid: true };
  }
};

/**
 * Debounce utility for input validation
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Format validation for display
 */
export const formatErrorMessage = (error: string | undefined): string => {
  if (!error) return '';

  // Capitalize first letter
  return error.charAt(0).toUpperCase() + error.slice(1);
};

/**
 * Real-time validation utilities
 */
export const validateEmailRealTime = (email: string): boolean => {
  if (!email.trim()) return true; // Allow empty in real-time
  return EMAIL_REGEX.test(email.trim());
};

export const validateDomainRealTime = (domain: string): boolean => {
  if (!domain.trim()) return true; // Allow empty in real-time
  return DOMAIN_REGEX.test(domain.trim());
};

export const validateTextInput = (value: string): boolean => {
  return value.trim().length > 0;
};

/**
 * Get validation rules for a specific step
 */
export const getStepValidationRules = (stepId: string) => {
  switch (stepId) {
    case 'company':
      return {
        required: ['company', 'domain'],
        patterns: {
          domain: DOMAIN_REGEX,
        },
        messages: {
          company: ERROR_MESSAGES.COMPANY_NAME_REQUIRED,
          domain: ERROR_MESSAGES.DOMAIN_REQUIRED,
        },
      };

    case 'locations':
    case 'services':
    case 'size':
    case 'industries':
    case 'jobTitles':
      return {
        required: [stepId],
        minItems: 1,
        message: `Please add at least one ${stepId.slice(0, -1)}`,
      };

    case 'emailSettings':
      return {
        required: ['emailSettings.signature'],
        conditional: {
          field: 'emailSettings.communicationStyle',
          value: 'OTHER',
          required: ['emailSettings.communicationStyleText'],
        },
        messages: {
          signature: ERROR_MESSAGES.EMAIL_SIGNATURE_REQUIRED,
          communicationStyleText: ERROR_MESSAGES.COMMUNICATION_STYLE_TEXT_REQUIRED,
        },
      };

    case 'invite':
      return {
        required: ['inviteUsers'],
        minItems: 1,
        message: ERROR_MESSAGES.INVITE_USER_REQUIRED,
        maxItems: 4,
        maxMessage: ERROR_MESSAGES.MAX_INVITE_USERS,
      };

    default:
      return {
        required: [],
        optional: true,
      };
  }
};
